import { makeAutoObservable, runInAction } from 'mobx'
import { api } from '@/shared/api/endpoints'
import type { FiatCurrency, InvoiceResponse, InvoiceStatus } from '@/shared/api/types'
import { compareDecimal, isPositive } from '@/shared/lib/decimal'
import { explain } from '@/shared/lib/errors'
import { Resource } from '@/stores/lib/Resource'
import type { UiStore } from '@/stores/UiStore'

const PAGE = 20

/** Потолок суммы у бекенда (`InvoiceCreate.amount_fiat`). */
const MAX_AMOUNT = '1000000'

/** Допуск недоплаты в процентах — те же границы, что в схеме заявки. */
const MAX_ACCURACY = 5

/** Пустой черновик ссылки. Валюта — доллар: цены магазинов в нём чаще. */
const EMPTY: Draft = { amount: '', fiat: 'usd', accuracy: '0', multiple: true }

/** Черновик платёжной ссылки. */
export interface Draft {
  amount: string
  fiat: FiatCurrency
  accuracy: string
  multiple: boolean
}

/** Как выглядит статус счёта для мерчанта. */
export const INVOICE_VIEW: Record<InvoiceStatus, { text: string; tone: string }> = {
  pending: { text: 'Валюта не выбрана', tone: 'soft' },
  waiting_payment: { text: 'Ждёт оплаты', tone: 'warn' },
  confirming: { text: 'Ждёт подтверждений', tone: 'info' },
  paid: { text: 'Оплачен', tone: 'ok' },
  overpaid: { text: 'Оплачен с переплатой', tone: 'ok' },
  underpaid_waiting: { text: 'Недоплата, ждёт доплаты', tone: 'warn' },
  underpaid: { text: 'Недоплачен', tone: 'bad' },
  expired: { text: 'Просрочен', tone: 'soft' },
}

/**
 * Выставленные счета.
 *
 * Ссылка на оплату создаётся на экране приёма и до сих пор существовала
 * ровно до перезагрузки страницы: найти её потом было негде. Мерчант,
 * закрывший вкладку, не мог ни отправить ссылку покупателю повторно,
 * ни узнать, оплатили ли её.
 */
export class InvoicesStore {
  readonly page: Resource<{ items: InvoiceResponse[]; total: number }>
  offset = 0

  /** Открыта ли форма создания. */
  creating = false
  draft: Draft = { ...EMPTY }
  errors: { amount?: string; accuracy?: string } = {}
  busy = false

  /**
   * Только что созданная ссылка.
   *
   * Показывается отдельным шагом, а не тостом: ссылку надо скопировать,
   * и закрывать окно раньше, чем человек это сделал, нельзя — второй
   * такой же ссылки не будет, а искать её потом придётся в списке.
   */
  created: InvoiceResponse | null = null

  private readonly ui: UiStore

  /**
   * Ключ идемпотентности живёт до успеха, а не до каждой попытки.
   *
   * Повтор после обрыва связи обязан попасть в ТУ ЖЕ заявку: иначе
   * магазин выставит покупателю две ссылки на одну покупку, и вторая
   * останется висеть неоплаченной.
   */
  private key: string | null = null

  constructor(ui: UiStore) {
    this.ui = ui
    this.page = new Resource(async () => {
      const d = await api.money.invoices({ limit: PAGE, offset: this.offset })
      return { items: d.items, total: d.total }
    })
    makeAutoObservable<this, 'ui'>(this, { ui: false })
  }

  get limit(): number {
    return PAGE
  }

  get items(): InvoiceResponse[] {
    return this.page.value?.items ?? []
  }

  openCreate(): void {
    this.draft = { ...EMPTY }
    this.errors = {}
    this.key = null
    this.creating = true
  }

  closeCreate(): void {
    this.creating = false
  }

  closeCreated(): void {
    this.created = null
  }

  patch(part: Partial<Draft>): void {
    this.draft = { ...this.draft, ...part }
    // Ошибка снимается с того поля, которое правят: висящая ошибка
    // под уже исправленной суммой читается как «всё ещё не так».
    if ('amount' in part) {
      const { amount: _drop, ...rest } = this.errors
      this.errors = rest
    }
    if ('accuracy' in part) {
      const { accuracy: _drop, ...rest } = this.errors
      this.errors = rest
    }
  }

  /** Создать ссылку. Возвращает `true`, если заявка появилась. */
  async submit(): Promise<boolean> {
    if (!this.validate() || this.busy) return false
    if (this.key === null) this.key = newKey()
    this.busy = true
    try {
      const invoice = await api.money.createInvoice({
        amount_fiat: this.draft.amount.trim().replace(',', '.'),
        fiat_currency: this.draft.fiat,
        accuracy_percent: this.draft.accuracy.trim() === ''
          ? '0'
          : this.draft.accuracy.trim().replace(',', '.'),
        allow_multiple_payments: this.draft.multiple,
      }, this.key)
      runInAction(() => {
        this.creating = false
        this.created = invoice
        this.key = null
      })
      // Список перечитывается всегда: созданная ссылка должна быть
      // видна и после закрытия окна с ней.
      this.offset = 0
      void this.page.load()
      return true
    } catch (error) {
      this.ui.failure(explain(error))
      return false
    } finally {
      runInAction(() => { this.busy = false })
    }
  }

  private validate(): boolean {
    const errors: { amount?: string; accuracy?: string } = {}
    const amount = this.draft.amount.trim().replace(',', '.')
    if (amount === '') errors.amount = 'Укажите сумму'
    else if (!/^\d+(\.\d+)?$/.test(amount)) errors.amount = 'Только цифры и точка'
    else if (!isPositive(amount)) errors.amount = 'Сумма должна быть больше нуля'
    else if ((amount.split('.')[1] ?? '').length > 2) {
      errors.amount = 'В деньгах не больше двух знаков после запятой'
    } else if ((compareDecimal(amount, MAX_AMOUNT) ?? 0) > 0) {
      errors.amount = 'Не больше 1 000 000'
    }

    const accuracy = this.draft.accuracy.trim().replace(',', '.')
    if (accuracy !== '') {
      if (!/^\d+(\.\d+)?$/.test(accuracy)) errors.accuracy = 'Только цифры и точка'
      else if ((compareDecimal(accuracy, String(MAX_ACCURACY)) ?? 0) > 0) {
        errors.accuracy = `Не больше ${MAX_ACCURACY}%`
      }
    }

    this.errors = errors
    return Object.keys(errors).length === 0
  }

  goToOffset(offset: number): void {
    this.offset = offset
    void this.page.load()
  }

  /**
   * Ссылка на оплату — та же, что получают магазины через API: бекенд
   * собирает её из PUBLIC_URL. Кабинет и API обязаны выдавать ОДНУ ссылку,
   * иначе мерчант сверяет две разные и не понимает, какая настоящая.
   * Пока PUBLIC_URL на сервере не задан, собираем от текущего домена.
   */
  linkFor(invoice: InvoiceResponse): string {
    return invoice.payment_url ?? `${globalThis.location.origin}/pay/${invoice.id}`
  }

  async copyLink(invoice: InvoiceResponse): Promise<void> {
    try {
      await navigator.clipboard.writeText(this.linkFor(invoice))
      this.ui.success('Ссылка скопирована')
    } catch {
      this.ui.failure('Не удалось скопировать')
    }
  }
}

/**
 * Ключ идемпотентности. `crypto.randomUUID` есть везде, кроме
 * незащищённого http — там откат на случайную строку: без ключа
 * бекенд не примет заявку вовсе.
 */
function newKey(): string {
  try {
    return globalThis.crypto.randomUUID()
  } catch {
    const bytes = new Uint8Array(16)
    globalThis.crypto.getRandomValues(bytes)
    return [...bytes].map((b) => b.toString(16).padStart(2, '0')).join('')
  }
}
